import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/navBar';
import LoadingAnimation from '../../components/LoadingAnimation';
import LikeButton from '../../components/LikeButton';
import { format } from 'date-fns';

export default function ClubPlayersPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerTitles, setPlayerTitles] = useState([]);
  const [clubTitles, setClubTitles] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [playerClubHistory, setPlayerClubHistory] = useState([]);
  const playerTitlesCache = useRef({});
  const playerClubHistoryCache = useRef({});

  useEffect(() => {
    const fetchClubData = async () => {
      try {
        // Fetch club details
        const clubResponse = await axios.get(`${process.env.REACT_APP_API_URL}/clubs/${id}`);
        setClub(clubResponse.data);

        // Fetch players
        const playersResponse = await axios.get(`${process.env.REACT_APP_API_URL}/players/club/${id}`);
        setPlayers(playersResponse.data);

        // Fetch club titles
        const titlesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/titles`);
        const titles = titlesResponse.data.filter(title => 
          title.clubId === parseInt(id) || title.winningClub?.clubId === parseInt(id)
        );
        setClubTitles(titles);

        // Fetch coaches
        const coachesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/coaches`);
        const coachesData = coachesResponse.data.data || coachesResponse.data || [];
        setCoaches(coachesData);

        // Get current user from localStorage
        const userId = localStorage.getItem('userId');
        if (userId) {
          setCurrentUser({ userId: parseInt(userId) });
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching club data:', error);
        setLoading(false);
      }
    };

    fetchClubData();
  }, [id]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 250);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Memoize filteredPlayers
  const filteredPlayers = useMemo(() => {
    return players.filter(player => 
      player.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      player.position.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
  }, [players, debouncedSearchTerm]);

  const fetchPlayerTitles = async (playerId) => {
    if (playerTitlesCache.current[playerId]) {
      setPlayerTitles(playerTitlesCache.current[playerId]);
      return;
    }
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/titles`);
      const titles = response.data.filter(title => 
        title.winningPlayers?.some(player => player.playerId === playerId)
      );
      playerTitlesCache.current[playerId] = titles;
      setPlayerTitles(titles);
    } catch (err) {
      console.error('Failed to fetch player titles:', err);
    }
  };

  const fetchPlayerClubHistory = async (playerId) => {
    if (playerClubHistoryCache.current[playerId]) {
      setPlayerClubHistory(playerClubHistoryCache.current[playerId]);
      return;
    }
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/player-clubs/history/${playerId}`);
      playerClubHistoryCache.current[playerId] = response.data;
      setPlayerClubHistory(response.data);
    } catch (err) {
      console.error('Failed to fetch player club history:', err);
      setPlayerClubHistory([]);
    }
  };

  const handlePlayerClick = async (player) => {
    setSelectedPlayer(player);
    await fetchPlayerTitles(player.playerId);
    await fetchPlayerClubHistory(player.playerId);
  };

  const handleCoachClick = (e, coachId) => {
    e.stopPropagation(); // Prevent any parent click
    navigate(`/sports/coach/${coachId}`);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-900 relative">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
            style={{ 
              backgroundImage: "url('https://t4.ftcdn.net/jpg/06/19/39/53/360_F_619395337_xfoBmAMfDhh0Wg5NRwVUUsZxJrUq8xBn.jpg')",
              zIndex: 0 
            }}
          />
          
          {/* Content Container */}
          <div className="relative z-10 container mx-auto px-4 py-8 min-h-screen">
            <LoadingAnimation type="club-banner" />
            <LoadingAnimation type="search" />
            <LoadingAnimation type="players" />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-900 relative">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
          style={{ 
            backgroundImage: "url('https://t4.ftcdn.net/jpg/06/19/39/53/360_F_619395337_xfoBmAMfDhh0Wg5NRwVUUsZxJrUq8xBn.jpg')",
            zIndex: 0 
          }}
        />
        
        {/* Content Container */}
        <div className="relative z-10 container mx-auto px-4 py-8 min-h-screen">
          {/* Club Banner */}
          {club && (
            <div className="bg-gray-900 rounded-2xl shadow-2xl overflow-hidden mb-12 mt-20">
              <div className="p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
                <div className="w-32 h-32 md:w-48 md:h-48 flex-shrink-0">
                  <img
                    src={club.logo || '/placeholder-club.png'}
                    alt={club.name}
                    className="w-full h-full object-contain rounded-full bg-gray-700 p-2"
                  />
                </div>
                <div className="flex-grow text-center md:text-left">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                    <h1 className="text-4xl md:text-5xl font-bold text-white">{club.name}</h1>
                    {currentUser && (
                      <LikeButton
                        entityId={club.clubId}
                        entityType="Club"
                        userId={currentUser.userId}
                        size="large"
                        className="justify-center md:justify-start"
                      />
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300">
                    <p className="flex items-center space-x-2">
                      <span className="text-red-500 font-semibold">Location:</span>
                      <span>{club.location}</span>
                    </p>
                    <p className="flex items-center space-x-2">
                      <span className="text-red-500 font-semibold">Founded:</span>
                      <span>{club.foundedYear}</span>
                    </p>
                    <p className="flex items-center space-x-2">
                      <span className="text-red-500 font-semibold">Players:</span>
                      <span>{players.length}</span>
                    </p>
                    {/* Coach Information */}
                    {coaches.length > 0 && (
                      <p className="flex items-center space-x-2">
                        <span className="text-red-500 font-semibold">Coach:</span>
                        <button
                          onClick={(e) => handleCoachClick(e, coaches[0].id)}
                          className="text-blue-400 hover:text-blue-300 underline transition-colors duration-200"
                        >
                          {coaches.length > 0 
                            ? `${coaches[0].firstName} ${coaches[0].lastName}`
                            : 'No coach assigned'
                          }
                        </button>
                      </p>
                    )}
                  </div>

                  {/* Club Titles Section */}
                  <div className="mt-6 bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                    <span className="text-red-500 font-semibold text-lg">Club Titles:</span>
                    {clubTitles.length > 0 ? (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {clubTitles.map((title) => (
                          <div key={title.titleId} className="bg-gray-700/50 p-3 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="text-white font-medium">{title.name}</h4>
                                <p className="text-gray-300 text-sm">{title.competition}</p>
                                <p className="text-gray-400 text-sm">Season: {title.season}</p>
                              </div>
                              <span className="text-gray-400 text-sm">
                                {format(new Date(title.dateWon), 'MMM dd, yyyy')}
                              </span>
                            </div>
                            {title.winningPlayers && title.winningPlayers.length > 0 && (
                              <p className="text-gray-400 text-sm mt-1">
                                Won with {title.winningPlayers.length} players
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-3 text-center py-6">
                        <div className="text-gray-400 text-lg mb-2">🏆</div>
                        <p className="text-gray-400">No titles won yet</p>
                        <p className="text-gray-500 text-sm mt-1">The club is still building its legacy</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="mb-8">
            <div className="max-w-md mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search players by name or position..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <div className="absolute right-3 top-3">
                  <svg
                    className="w-6 h-6 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Players Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-[1920px] mx-auto">
            {filteredPlayers.map((player) => (
              <div 
                key={player.playerId} 
                onClick={() => handlePlayerClick(player)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handlePlayerClick(player);
                  }
                }}
                className="group bg-gray-900 backdrop-blur-sm rounded-xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:transform hover:scale-[1.02] cursor-pointer border border-gray-700/50 hover:border-red-500/50"
              >
                <div className="p-8 space-y-6 h-full">
                  <div className="flex items-center space-x-6">
                    <div className="w-20 h-20 flex-shrink-0">
                      <img
                        src={player.profileImageUrl || '/placeholder-player.png'}
                        alt={player.name}
                        className="w-full h-full object-cover rounded-full bg-gray-700 group-hover:ring-2 group-hover:ring-red-500 transition-all duration-300"
                      />
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-start justify-between">
                        <div>
                          <h2 className="text-white font-semibold text-xl mb-2 group-hover:text-red-500 transition-colors duration-300">
                            {player.name}
                          </h2>
                          <div className="space-y-2">
                            <p className="text-gray-300 flex items-center space-x-2">
                              <span className="text-red-500">Position:</span> {player.position}
                            </p>
                            <p className="text-gray-300 flex items-center space-x-2">
                              <span className="text-red-500">Age:</span> {player.age}
                            </p>
                          </div>
                        </div>
                        {currentUser && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <LikeButton
                              entityId={player.playerId}
                              entityType="Player"
                              userId={currentUser.userId}
                              size="small"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-gray-300 bg-gray-800/50 p-4 rounded-lg mt-4 group-hover:bg-gray-800/70 transition-colors duration-300">
                    {player.bio || 'No bio available'}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Player Details Modal */}
          {selectedPlayer && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
              <div className="bg-gray-900 backdrop-blur-sm rounded-xl max-w-4xl w-full p-8 relative border border-gray-700 shadow-2xl">
                <button 
                  onClick={() => setSelectedPlayer(null)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-full p-2 transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="w-full md:w-1/3">
                    <div className="relative overflow-hidden rounded-xl bg-gray-700 p-4">
                      <img 
                        src={selectedPlayer.profileImageUrl || '/placeholder-player.png'} 
                        alt={selectedPlayer.name}
                        className="w-full h-80 object-cover rounded-lg hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </div>
                  
                  <div className="w-full md:w-2/3 text-white space-y-6">
                    <div className="flex items-start justify-between">
                      <h2 className="text-3xl font-bold text-white">{selectedPlayer.name}</h2>
                      {currentUser && (
                        <LikeButton
                          entityId={selectedPlayer.playerId}
                          entityType="Player"
                          userId={currentUser.userId}
                          size="large"
                        />
                      )}
                    </div>
                    <div className="space-y-4">
                      <div className="bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                        <span className="text-red-500 font-semibold">Position:</span>
                        <span className="ml-2 text-gray-200">{selectedPlayer.position}</span>
                      </div>
                      <div className="bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                        <span className="text-red-500 font-semibold">Age:</span>
                        <span className="ml-2 text-gray-200">{selectedPlayer.age}</span>
                      </div>
                      {selectedPlayer.preferredLeg && (
                        <div className="bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                          <span className="text-red-500 font-semibold">Preferred Leg:</span>
                          <span className="ml-2 text-gray-200 capitalize">{selectedPlayer.preferredLeg}</span>
                        </div>
                      )}
                      {selectedPlayer.bio && (
                        <div className="bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                          <span className="text-red-500 font-semibold">Bio:</span>
                          <p className="mt-2 text-gray-200 leading-relaxed">{selectedPlayer.bio}</p>
                        </div>
                      )}
                      {/* Club History Section */}
                      {playerClubHistory.length > 0 && (
                        <div className="bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                          <span className="text-red-500 font-semibold">Club History:</span>
                          <div className="mt-2 space-y-2">
                            {playerClubHistory.map((history) => (
                              <div key={history.id} className="flex items-center gap-4 bg-gray-700/50 p-3 rounded-lg">
                                <img
                                  src={history.club.logo || '/placeholder-club.png'}
                                  alt={history.club.name}
                                  className="w-10 h-10 object-cover rounded-full border border-gray-600"
                                />
                                <div>
                                  <div className="text-white font-medium">{history.club.name}</div>
                                  <div className="text-gray-400 text-sm">{history.club.location}</div>
                                  <div className="text-gray-400 text-xs">
                                    {history.startDate ? `From: ${format(new Date(history.startDate), 'MMM yyyy')}` : ''}
                                    {history.endDate ? ` - To: ${format(new Date(history.endDate), 'MMM yyyy')}` : ' - Present'}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Club Section */}
                      {club && (
                        <div className="bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                          <span className="text-red-500 font-semibold">Club:</span>
                          <div className="mt-2">
                            <p className="text-gray-200 font-medium">{club.name}</p>
                            {club.location && (
                              <p className="text-gray-400 text-sm mt-1">{club.location}</p>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Titles Section */}
                      {playerTitles.length > 0 && (
                        <div className="bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                          <span className="text-red-500 font-semibold">Titles Won:</span>
                          <div className="mt-2 space-y-2">
                            {playerTitles.map((title) => (
                              <div key={title.titleId} className="bg-gray-700/50 p-3 rounded-lg">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <h4 className="text-white font-medium">{title.name}</h4>
                                    <p className="text-gray-300 text-sm">{title.competition}</p>
                                    <p className="text-gray-400 text-sm">Season: {title.season}</p>
                                  </div>
                                  <span className="text-gray-400 text-sm">
                                    {format(new Date(title.dateWon), 'MMM dd, yyyy')}
                                  </span>
                                </div>
                                {title.winningClub && (
                                  <p className="text-gray-400 text-sm mt-1">
                                    with {title.winningClub.name}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
} 